import { TimeSheetEntryTypeEnum, WeekDaysEnum } from "@oysterhr/schema"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import { WorkScheduleFormLegacy } from "./WorkScheduleFormLegacy"
import { Icon, Button, Text, Badge } from "@oysterhr/ui"
import { UseFormReturn } from "react-hook-form"
import { HireWorkDaysFormValues, WorkDaysFormValues } from "./types"
import { WorkScheduleFormProps, WorkScheduleFormRow } from "./WorkScheduleFormRow"
import { DAYS } from "./constants"
import { sumWorkedHours } from "./utils"
import { formatDurationToHoursMinutes } from "@oysterhr/common"
import { HireWorkScheduleForm, HireWorkScheduleFormProps } from "./HireWorkScheduleForm"

type DaysSelectorProps = {
  selectedDays: string[]
  onDayClick: (day: WeekDaysEnum, selected: boolean) => void
  onClearAll?: () => void
  className?: string
}
export const DaysSelector = ({
  selectedDays,
  onDayClick,
  onClearAll,
  className,
}: DaysSelectorProps) => {
  const { t } = useTranslation("WorkScheduleComponents")

  return (
    <div className={classNames("flex items-center", className)}>
      {DAYS.map(day => {
        const selected = selectedDays.includes(day)

        return (
          <button
            key={day}
            type="button"
            className={classNames("mr-4 h-10 w-10 rounded-lg", {
              "bg-primary-base text-white": selected,
              "border border-gray-200 bg-gray-100 text-gray-400": !selected,
            })}
            id={`checkbox-${day}`}
            onClick={() => onDayClick(day, selected)}
          >
            {t(`weekdays.short.${day}`)}
          </button>
        )
      })}
      {onClearAll && (
        <Button onClick={onClearAll} variation="borderless" size="sm" type="button">
          <Text size="tiny" className="text-primary-base">
            {t("clearSelection")}
          </Text>
        </Button>
      )}
    </div>
  )
}

export type HireHoursSelectorProps = {
  selectedDays: string[]
  onCopyToAllClick: () => void
  form: UseFormReturn<HireWorkDaysFormValues>
} & Pick<HireWorkScheduleFormProps, "onWorkScheduleChange" | "isClearable">
export const HireHoursSelector = ({
  selectedDays,
  onCopyToAllClick,
  form,
  isClearable = true,
  onWorkScheduleChange,
}: HireHoursSelectorProps) => {
  const { t } = useTranslation("WorkScheduleComponents")
  const {
    formState: { errors },
  } = form

  return (
    <div className="flex items-start space-x-2">
      <div className="space-y-4">
        {selectedDays.map(day => {
          return (
            <HireWorkScheduleForm
              isClearable={isClearable}
              key={day}
              day={day as WeekDaysEnum}
              errors={errors.workDays?.[day as WeekDaysEnum]}
              form={form}
              onWorkScheduleChange={onWorkScheduleChange}
            />
          )
        })}
      </div>

      {selectedDays.length > 1 && (
        <Button
          onClick={onCopyToAllClick}
          variation="borderless"
          size="sm"
          type="button"
          startAdornment={<Icon name="Copy" size="sm" />}
        >
          <Text size="tiny" className="text-primary-base">
            {t("copyToAll")}
          </Text>
        </Button>
      )}
    </div>
  )
}

export type HoursSelectorLegacyProps = {
  selectedDays: string[]
  onCopyToAllClick: () => void
  form: UseFormReturn<WorkDaysFormValues>
} & Pick<WorkScheduleFormProps, "onWorkScheduleChange" | "isClearable">
export const HoursSelectorLegacy = ({
  selectedDays,
  onCopyToAllClick,
  form,
  isClearable = true,
  onWorkScheduleChange,
}: HoursSelectorLegacyProps) => {
  const { t } = useTranslation("WorkScheduleComponents")
  const {
    formState: { errors },
  } = form

  return (
    <div className="flex items-start space-x-2">
      <div className="space-y-4">
        {selectedDays.map(day => {
          return (
            <WorkScheduleFormLegacy
              isClearable={isClearable}
              key={day}
              day={day as WeekDaysEnum}
              errors={errors.workDays?.[day as WeekDaysEnum]?.[0]}
              form={form}
              onWorkScheduleChange={onWorkScheduleChange}
            />
          )
        })}
      </div>

      {selectedDays.length > 1 && (
        <Button
          onClick={onCopyToAllClick}
          variation="borderless"
          size="sm"
          type="button"
          startAdornment={<Icon name="Copy" size="sm" />}
        >
          <Text size="tiny" className="text-primary-base">
            {t("copyToAll")}
          </Text>
        </Button>
      )}
    </div>
  )
}

export type HoursSelectorProps = {
  selectedDays: string[]
  onCopyToAllClick: () => void
  form: UseFormReturn<WorkDaysFormValues>
  availableEntryTypes: TimeSheetEntryTypeEnum[]
} & Pick<WorkScheduleFormProps, "onWorkScheduleChange" | "isClearable">
export const HoursSelector = ({
  selectedDays,
  onCopyToAllClick,
  form,
  isClearable = true,
  onWorkScheduleChange,
  availableEntryTypes,
}: HoursSelectorProps) => {
  return (
    <div className="mt-2 flex items-start space-x-2">
      <div className="w-full space-y-4">
        {selectedDays.map((day, index) => {
          return (
            <MultipleHoursSelector
              day={day as WeekDaysEnum}
              key={day}
              isClearable={isClearable}
              onWorkScheduleChange={onWorkScheduleChange}
              form={form}
              onCopyToAllClick={
                selectedDays.length > 1 && index === 0 ? onCopyToAllClick : undefined
              }
              availableEntryTypes={availableEntryTypes}
            />
          )
        })}
      </div>
    </div>
  )
}

export type MultipleHoursSelectorProps = {
  onCopyToAllClick: (() => void) | undefined
  form: UseFormReturn<WorkDaysFormValues>
  day: WeekDaysEnum
  availableEntryTypes: TimeSheetEntryTypeEnum[]
} & Pick<WorkScheduleFormProps, "onWorkScheduleChange" | "isClearable">
const MultipleHoursSelector = ({
  day,
  form,
  onWorkScheduleChange,
  onCopyToAllClick,
  availableEntryTypes,
}: MultipleHoursSelectorProps) => {
  const { t } = useTranslation("WorkScheduleForm")
  const {
    formState: { errors },
    getValues,
    setValue,
  } = form
  const daysHoursCount = getValues(`workDays.${day}`)?.length || 1
  const totalWorkDuration = sumWorkedHours(getValues(`workDays.${day}`) || [])
  const totalWorkDurationText = formatDurationToHoursMinutes(totalWorkDuration)

  return (
    <div
      className="flex flex-col rounded-lg border border-gray-400 bg-white p-4"
      data-testid={`timeRecords-${day}`}
    >
      <Text size="large" className="font-medium">
        {t(`form.weekdays.longPlural.${day}`)}
      </Text>
      <div className="p-2">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-sm font-medium">{t("table.workTime")}</th>
                <th className="text-sm font-medium">{t("table.workType")}</th>
                <th className="sticky right-0 min-w-32 bg-white text-right text-sm font-medium">
                  {t("table.totalWorkTime")}
                </th>
              </tr>
            </thead>
            <tbody className="border-spacing-y-2 divide-y">
              {Array.from({ length: daysHoursCount }).map((_, index) => (
                <WorkScheduleFormRow
                  isClearable={daysHoursCount > 1}
                  index={index}
                  key={index}
                  day={day as WeekDaysEnum}
                  errors={errors.workDays?.[day as WeekDaysEnum]?.[index]}
                  form={form}
                  onWorkScheduleChange={onWorkScheduleChange}
                  availableEntryTypes={availableEntryTypes}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-2 flex justify-between border-t pt-2">
        <div className="flex gap-2">
          <Button
            startAdornment={<Icon name="PlusCircle" size="sm" />}
            onClick={() =>
              setValue(`workDays.${day}`, [
                ...(getValues(`workDays.${day}`) || []),
                { from: "", to: "", break: "", entryType: "" },
              ])
            }
            size="sm"
            variation="outline"
          >
            {t("form.addTimeRecord")}
          </Button>
          {onCopyToAllClick && (
            <Button
              onClick={onCopyToAllClick}
              variation="borderless"
              size="sm"
              type="button"
              startAdornment={<Icon name="Copy" size="sm" />}
              className="!bg-gray-100"
            >
              <Text size="tiny" className="text-primary-base">
                {t("copyToAll")}
              </Text>
            </Button>
          )}
        </div>
        <Badge size="sm" variation="gray" data-testid={`totalHours-${day}`}>
          <Icon name="Clock" size="xs" />
          {totalWorkDurationText}
        </Badge>
      </div>
    </div>
  )
}
